import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {MatIconModule} from "@angular/material/icon";
import {MatMenuModule} from "@angular/material/menu";
import {MatButtonModule} from "@angular/material/button";
import {
    AbstractControl,
    ControlValueAccessor,
    FormBuilder,
    FormGroup,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR, ReactiveFormsModule, ValidationErrors
} from "@angular/forms";
import {filter, map, startWith} from "rxjs/operators";
import {arrayIsSet, isNotNullOrUndefined} from "../../providers/service/global-variables.service";
import {combineLatest, Observable, Subscription} from "rxjs";
import {TranslateModule} from "@ngx-translate/core";
import {PipesModule} from "../../providers/pipes/pipes.module";
import {AsyncPipe, NgClass, NgTemplateOutlet} from "@angular/common";
import {MatButtonToggle, MatButtonToggleGroup} from "@angular/material/button-toggle";
import {MatBadge} from "@angular/material/badge";

export class ButtonFloatingType {
    constructor(objectId: string, registerValue: any, traduction: string | undefined = undefined, notTraduction: string | undefined = undefined, sensorDataTraduction: string | undefined = undefined, icon: string | undefined = undefined, svgIcon: string | undefined = undefined) {
        this.objectId = objectId
        this.registerValue = registerValue
        this.traduction = traduction;
        this.notTraduction = notTraduction;
        this.sensorDataTraduction = sensorDataTraduction;
        this.icon = icon;
        this.svgIcon = svgIcon;
    }

    objectId: string;
    registerValue: any;
    traduction?: string;
    notTraduction?: string;
    sensorDataTraduction?: string;
    icon?: string;
    svgIcon?: string;
}

@Component({
    selector: 'app-floating-buttons',
    imports: [
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        TranslateModule,
        PipesModule,
        AsyncPipe,
        NgTemplateOutlet,
        MatButtonToggle,
        MatButtonToggleGroup,
        ReactiveFormsModule,
        NgClass,
        MatBadge
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FloatingButtonsComponent
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FloatingButtonsComponent
        }
    ],
    templateUrl: './floating-buttons.component.html',
    styleUrl: './floating-buttons.component.scss'
})
export class FloatingButtonsComponent implements OnInit, OnDestroy, ControlValueAccessor {
    @Input() title: string | undefined
    @Input() mode = 'iconMode'
    @Input() color = 'primary'
    @Input() multiple = false;
    @Input() shape = 'mat-flat-button';
    @Input() possibleValues: ButtonFloatingType[] | undefined
    @Input() disableButton = false
    @Output() clickButton = new EventEmitter<ButtonFloatingType>()
    public form: FormGroup
    currentObjectId$: Observable<string>
    currentpValue$: Observable<ButtonFloatingType | undefined>
    registerValue$: Observable<any | undefined>
    subscriptions = new Subscription();
    currentObjectIds$: Observable<string[] | undefined>
    numberSelected$: Observable<number | undefined>
    currentpValues$: Observable<ButtonFloatingType[] | undefined>
    registerValues$: Observable<any[] | undefined>

    constructor(fb: FormBuilder,) {
        this.form = fb.group({objectIds: undefined,})
        this.currentObjectId$ = this.form.get('objectIds')!.valueChanges.pipe(
            startWith(undefined),
            filter(id => this.multiple == false),
            map(() => this.form.get('objectIds')?.value)
        )
        this.currentpValue$ = this.currentObjectId$.pipe(
            map((objectId) => {
                const pValue = this.possibleValues?.find(pVal => pVal.objectId === objectId)
                if (pValue) {
                    return pValue
                } else {
                    return undefined
                }
            })
        )
        this.registerValue$ = this.currentpValue$.pipe(
            map((pValue) => {
                if (pValue) {
                    return pValue.registerValue
                } else {
                    return undefined
                }
            })
        )
        this.currentObjectIds$ = this.form.get('objectIds')!.valueChanges.pipe(
            startWith(undefined),
            filter(id => this.multiple == true),
            map(() => this.form.get('objectIds')?.value)
        )
        this.numberSelected$ = this.currentObjectIds$.pipe(
            map(values => this.multiple && arrayIsSet(values) ? values.length : undefined)
        )
        this.currentpValues$ = this.currentObjectIds$.pipe(
            map((objectIds) => {
                if (arrayIsSet(this.possibleValues) && arrayIsSet(objectIds)) {
                    return this.possibleValues?.filter(pVal => objectIds.includes(pVal.objectId))
                } else {
                    return undefined
                }
            })
        )
        this.registerValues$ = this.currentpValues$.pipe(
            map((pValues) => {
                if (arrayIsSet(pValues)) {
                    return pValues.map(pValue => pValue.registerValue)
                } else {
                    return undefined
                }
            })
        )
    }

    ngOnInit(): void {
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    onTouched: Function = () => {
    };

    registerOnChange(onChange: any) {
        const values$ = combineLatest([this.registerValue$.pipe(
            startWith(undefined)
        ), this.registerValues$.pipe(
            startWith(undefined)
        )]).pipe(
            map(([registerValue, registerValues]) => {
                if (this.multiple) {
                    return registerValues
                } else {
                    return registerValue
                }
            })
        )
        this.subscriptions.add(
            values$.subscribe(onChange)
        );

    }

    setDisabledState(disabled: boolean) {
        if (disabled) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }

    writeValue(value: any) {
        if (isNotNullOrUndefined(value) && (!this.multiple || this.multiple && arrayIsSet(value))) {
            setTimeout(() => {
                if (this.multiple) {
                    if (arrayIsSet(value)) {
                        value.forEach((v: any) => {
                            if (typeof v == "string") {
                                this.form.get('objectIds')?.setValue(v)
                            } else if (v.objectId) {
                                this.form.get('objectIds')?.setValue(v.objectId)
                            } else if (v.toString) {
                                this.form.get('objectIds')?.setValue(v.toString())
                            }
                        })
                    }
                } else {
                    if (typeof value == "string") {
                        this.form.get('objectIds')?.setValue(value)
                    } else if (value.objectId) {
                        this.form.get('objectIds')?.setValue(value.objectId)
                    } else if (value.toString) {
                        this.form.get('objectIds')?.setValue(value.toString())
                    }
                }
            },)
        } else {
            this.form.reset()
        }
    }

    validate(control: AbstractControl): ValidationErrors | null {
        // if (!isNotNullOrUndefined(this.form.get('autoComplete')?.validator)) {
        //     this.form.get('autoComplete')?.setValidators(control.validator)
        //     this.form.get('autoComplete')?.updateValueAndValidity()
        // }
        if (this.form.valid) {
            return null
        } else {
            return this.form.errors!;
        }
    }

    addControlErrors(allErrors: any, controlName: string) {
        const errors = {...allErrors};
        const controlErrors = this.form.controls[controlName].errors;
        if (controlErrors) {
            errors[controlName] = controlErrors;
        }
        return errors;
    }

    registerOnTouched(onTouched: Function) {
        this.onTouched = onTouched;
    }

    setValue(objectId: string) {
        if (this.multiple) {
            const currentIds = arrayIsSet(this.form.get('objectIds')?.value) ? [...this.form.get('objectIds')?.value] : []
            const index = currentIds.findIndex(pValue => pValue == objectId)
            if (index >= 0) {
                currentIds.splice(index, 1)
            } else {
                currentIds.push(objectId)
            }
            this.form.get('objectIds')?.setValue(currentIds)
        } else {
            this.form.get('objectIds')?.setValue(objectId)
        }
    }

    isEqualsPredicate(pValue: ButtonFloatingType, objectId: string | undefined, objectIds: string[] | undefined) {
        if (objectId) {
            return pValue.objectId == objectId;
        } else if (arrayIsSet(objectIds)) {
            return objectIds.includes(pValue.objectId);
        } else {
            return false
        }
    }
}
